import { useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import type { NavigateOptions } from 'react-router-dom';

import { getSecureRedirectUrl } from '@/auth/utils/secure-redirect';
import { useNavigateTo } from './use-navigate-to';

interface AppRedirectOptions extends NavigateOptions {
  fallbackPath?: string;
}

export const useAppRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigateTo = useNavigateTo();

  const redirectToIntended = useCallback(
    (options: AppRedirectOptions = {}) => {
      const { fallbackPath = '/home', ...navigateOptions } = options;

      const rawRedirectTo = searchParams.get('redirectTo');
      const secureRedirectUrl = getSecureRedirectUrl(rawRedirectTo);

      if (secureRedirectUrl) {
        if (secureRedirectUrl.startsWith('/')) {
          navigate(secureRedirectUrl, { replace: true, ...navigateOptions });
        } else {
          try {
            const url = new URL(secureRedirectUrl);
            navigate(url.pathname + url.search + url.hash, {
              replace: true,
              ...navigateOptions,
            });
          } catch {
            navigate(fallbackPath, { replace: true, ...navigateOptions });
          }
        }
      } else {
        navigate(fallbackPath, { replace: true, ...navigateOptions });
      }
    },
    [navigate, searchParams],
  );

  const redirectToLogin = useCallback(
    (options?: NavigateOptions) => {
      const from = location.pathname + location.search + location.hash;

      if (from === '/') {
        navigateTo.signIn(undefined, undefined, options);
      } else {
        navigateTo.signIn(
          undefined,
          `redirectTo=${encodeURIComponent(from)}`,
          options,
        );
      }
    },
    [location, navigateTo],
  );

  const redirectToSecurePath = useCallback(
    (path: string, options: AppRedirectOptions = {}) => {
      const { fallbackPath = '/home', ...navigateOptions } = options;

      const secureUrl = getSecureRedirectUrl(path);

      if (secureUrl) {
        if (secureUrl.startsWith('/')) {
          navigate(secureUrl, navigateOptions);
        } else {
          try {
            const url = new URL(secureUrl);
            navigate(url.pathname + url.search + url.hash, navigateOptions);
          } catch {
            navigate(fallbackPath, { replace: true, ...navigateOptions });
          }
        }
      } else {
        navigate(fallbackPath, { replace: true, ...navigateOptions });
      }
    },
    [navigate],
  );

  const buildRedirectUrl = useCallback(
    (targetPath: string, currentPath?: string): string => {
      const pathToRedirect = currentPath || location.pathname + location.search;
      return `${targetPath}?redirectTo=${encodeURIComponent(pathToRedirect)}`;
    },
    [location],
  );

  return {
    buildRedirectUrl,
    redirectToIntended,
    redirectToLogin,
    redirectToSecurePath,
  };
};
